import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { ControlledMenu, MenuItem, useClick } from '@szhsin/react-menu';
import styled from 'styled-components';

import { GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES } from '@/common/actions/get';

import { COLORS, OrderTypes } from '@/constants';
import { Box, InputV2, Typography } from '@/elements';
import { Color } from '@/utils';

import '@szhsin/react-menu/dist/index.css';

const MENU_MAX_HEIGHT = '300px';

const StyledControlledMenu = styled(ControlledMenu)`
  ul {
      border-radius: ${({ $borderRadius }) => `${$borderRadius}px`};
      max-height: ${MENU_MAX_HEIGHT};
      overflow-y: auto;
      z-index: 1000;
`;

const StyledMenuItem = styled(MenuItem)`
  &.szh-menu__item {
    color: ${({ color }) => color};
  }
  &.szh-menu__item--hover {
    background: ${() => Color.lightGrayHover};
    color: ${({ color }) => (color ? color : Color.primary)};
  }
`;

export const DropdownSearch = ({
  children,
  onClick,
  borderRadius = 8,
  isSearchable,
  defaultOptions,
  row,
}) => {
  const ref = useRef(null);
  const isOpenRef = useRef(false);

  const [isOpen, setOpen] = useState(false);
  const anchorProps = useClick(isOpen, setOpen);
  const [search, setSearch] = useState('');

  const [getPartnerClientAccounts, { data }] = useLazyQuery(GET_PARTNER_CLIENT_ACCOUNT_EMPLOYEES, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    isOpenRef.current = isOpen;
    setSearch(!isOpen ? '' : search);
  }, [isOpen]);

  const handleSearchChange = async (value) => {
    setSearch(value);

    await getPartnerClientAccounts({
      variables: {
        partnerClientAccountId: row?.original?.id,
        filterEmail: value,
        limit: 1000,
        status: 'ACTIVE',
        deletedAt: null,
        sortEmail: OrderTypes.Asc,
      },
    });
  };

  const BadgeComponent = () => (
    <Box width="25px" height="25px" justify="center" align="center" background={Color.primary}>
      <Typography variant="default" color={COLORS.backgroundLight}>
        A
      </Typography>
    </Box>
  );

  const options = useMemo(
    () => [
      ...(data?.partnerClientAccountEmployees?.items
        ?.filter(({ deletedAt, status }) => !deletedAt && status === 'ACTIVE')
        ?.map(({ email, id, isInDefaultGroup }) => ({
          value: email,
          label: isInDefaultGroup ? (
            <Box gap="8px">
              <Typography>{email}</Typography>

              <Typography>{BadgeComponent()}</Typography>
            </Box>
          ) : (
            <Typography>{email}</Typography>
          ),
          id,
        })) || []),
    ],
    [data?.partnerClientAccountEmployees?.items]
  );

  return (
    <>
      <Box ref={ref} {...anchorProps}>
        {children}
      </Box>

      <StyledControlledMenu
        $borderRadius={borderRadius}
        state={isOpen ? 'open' : 'closed'}
        anchorRef={ref}
        onClose={() => setOpen(false)}
      >
        <Box margin="22px">
          <Typography color={COLORS.primary600} variant="default">
            Login as
          </Typography>
        </Box>

        {isSearchable && (
          <Box margin="22px">
            <InputV2
              value={search}
              isAutoFocus
              placeholder="Filter"
              onChange={(e) =>
                typeof e === 'number'
                  ? handleSearchChange(e.toString())
                  : handleSearchChange(e.target.value)
              }
            />
          </Box>
        )}

        {(search ? options : defaultOptions)?.map((option) => (
          <StyledMenuItem
            onClick={() => onClick(option)}
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            // @ts-ignore
            color={option.color}
          >
            {option.label}
          </StyledMenuItem>
        ))}
      </StyledControlledMenu>
    </>
  );
};
